/**
 * ============================================================================
 * MAKE OFFER COMPONENT
 * ============================================================================
 * 
 * Make Offer Screen
 */

import React, { useState } from 'react';
import { useDispute } from '../../hooks/useDispute';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Card from '../common/Card';
import Textarea from '../common/Textarea';
import ProgressBar from '../common/ProgressBar';

/**
 * Make Offer Component
 * @param {Object} props
 * @param {string} props.disputeId - Dispute ID
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSuccess - Success callback
 * @returns {JSX.Element}
 */
const MakeOffer = ({ disputeId, onClose, onSuccess }) => {
  const { makeOffer, loading } = useDispute();
  const { showError, showSuccess } = useToast();
  const { formatCurrency } = useI18n();

  const [formData, setFormData] = useState({
    offerType: 'partial_refund',
    refundPercentage: 40,
    message: '',
  });

  const offerTypes = [
    { id: 'full_refund', label: 'Full refund', description: '100% to buyer' },
    { id: 'partial_refund', label: 'Partial refund', description: 'Custom percentage' },
    { id: 'revision_refund', label: 'Revision + partial refund', description: 'Revise work + refund' },
    { id: 'no_refund', label: 'No refund', description: 'Fulfill original terms' },
  ];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      const offerData = {
        offerType: formData.offerType,
        refundPercentage: formData.offerType === 'partial_refund' ? formData.refundPercentage : null,
        message: formData.message.trim() || undefined,
      };

      await makeOffer(disputeId, offerData);
      showSuccess('Offer sent successfully!');
      if (onSuccess) onSuccess();
    } catch (error) {
      showError(error.message || 'Failed to send offer');
    }
  };

  // Calculate amounts (placeholder - would come from dispute)
  const totalAmount = 50750;
  const buyerAmount = formData.offerType === 'full_refund'
    ? totalAmount
    : formData.offerType === 'partial_refund'
    ? (totalAmount * formData.refundPercentage) / 100
    : 0;
  const sellerAmount = totalAmount - buyerAmount;

  return (
    <Card variant="glass" className="animate-scaleIn">
      <div className="mb-6">
        <button
          onClick={onClose}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-all group"
        >
          <svg
            className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="font-medium">Back</span>
        </button>
        <h2 className="text-3xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
          Make Offer
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Propose a solution to resolve this dispute
        </p>
      </div>

      <div className="space-y-6">
        {/* Offer Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Propose a solution:
          </label>
          <div className="space-y-3">
            {offerTypes.map((type) => (
              <label
                key={type.id}
                className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  formData.offerType === type.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <input
                  type="radio"
                  name="offerType"
                  value={type.id}
                  checked={formData.offerType === type.id}
                  onChange={(e) => handleChange('offerType', e.target.value)}
                  className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {type.label}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {type.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Partial Refund Slider */}
        {formData.offerType === 'partial_refund' && (
          <Card variant="outlined" className="bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20">
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                  Refund Percentage
                </label>
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formData.refundPercentage}%
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="100"
                value={formData.refundPercentage}
                onChange={(e) => handleChange('refundPercentage', parseInt(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-blue-600"
              />
              <div className="mt-4 p-4 bg-white/80 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Buyer receives:</span>
                  <span className="text-lg font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(buyerAmount, 'KES')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Seller keeps:</span>
                  <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {formatCurrency(sellerAmount, 'KES')}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Message */}
        <div>
          <Textarea
            label="Add message (optional)"
            value={formData.message}
            onChange={(e) => handleChange('message', e.target.value)}
            placeholder="I can accept 40% refund to close this..."
            rows={4}
            maxLength={500}
          />
        </div>

        {/* Submit Button */}
        <Button
          variant="gradient"
          size="xl"
          fullWidth
          onClick={handleSubmit}
          loading={loading}
          className="text-lg py-6 shadow-xl hover:shadow-2xl transition-all mt-6"
        >
          Send Offer →
        </Button>
      </div>
    </Card>
  );
};

export default MakeOffer;
